import { Injectable, signal } from '@angular/core';

export type PaymentRequiredSource = 'sse' | 'polling';

export type PaymentRequiredRequest =
  | {
      source: PaymentRequiredSource;
      kind: 'prompt';
      conversationId: string | null;
      tempConversationId: string;
      promptText: string;
      model?: string;
      deepResearch?: boolean;
    }
  | {
      source: PaymentRequiredSource;
      kind: 'polling';
      conversationId: string;
    };

export type PaymentRequiredResult = { kind: 'completed' } | { kind: 'cancelled' };

type Deferred<T> = { promise: Promise<T>; resolve: (value: T) => void };

function createDeferred<T>(): Deferred<T> {
  let resolve!: (value: T) => void;
  const promise = new Promise<T>((r) => {
    resolve = r;
  });
  return { promise, resolve };
}

type QueueEntry = { request: PaymentRequiredRequest; deferred: Deferred<PaymentRequiredResult> };

function requestKey(req: PaymentRequiredRequest): string {
  return `${req.kind}:${req.conversationId ?? ''}`;
}

@Injectable({ providedIn: 'root' })
export class PaymentRequiredService {
  readonly isOpen = signal(false);
  readonly request = signal<PaymentRequiredRequest | null>(null);

  private inFlight?: { deferred: Deferred<PaymentRequiredResult>; request: PaymentRequiredRequest };
  private pendingQueue: QueueEntry[] = [];

  open(req: PaymentRequiredRequest): Promise<PaymentRequiredResult> {
    if (this.inFlight) {
      const d = createDeferred<PaymentRequiredResult>();
      this.pendingQueue.push({ request: req, deferred: d });
      return d.promise;
    }

    this.request.set(req);
    this.isOpen.set(true);
    const d = createDeferred<PaymentRequiredResult>();
    this.inFlight = { deferred: d, request: req };
    return d.promise;
  }

  complete(): void {
    const current = this.inFlight;
    if (!current) return;

    const completedKey = requestKey(current.request);
    this.close();
    current.deferred.resolve({ kind: 'completed' });

    const duplicates: QueueEntry[] = [];
    const remaining: QueueEntry[] = [];
    for (const entry of this.pendingQueue) {
      if (requestKey(entry.request) === completedKey) {
        duplicates.push(entry);
      } else {
        remaining.push(entry);
      }
    }
    this.pendingQueue = remaining;

    for (const dup of duplicates) {
      dup.deferred.resolve({ kind: 'completed' });
    }

    this.drainQueue();
  }

  cancel(): void {
    const current = this.inFlight;
    if (!current) return;
    this.close();
    current.deferred.resolve({ kind: 'cancelled' });

    for (const entry of this.pendingQueue) {
      entry.deferred.resolve({ kind: 'cancelled' });
    }
    this.pendingQueue = [];
  }

  private close(): void {
    this.isOpen.set(false);
    this.request.set(null);
    this.inFlight = undefined;
  }

  private drainQueue(): void {
    const next = this.pendingQueue.shift();
    if (!next) return;

    this.request.set(next.request);
    this.isOpen.set(true);
    this.inFlight = { deferred: next.deferred, request: next.request };
  }
}
